import React from 'react';

import type { FieldRenderProps } from '@/js/libs/final-form';
import { Input } from '@/js/libs/ui';

import { Container, Counter, Prefix } from './styles';

export interface Settings {
  intOnly?: boolean;
  floatOnly?: boolean;
  regexp?: string | RegExp;
  callback?: (v: string) => string;
  maxLength?: number;
  prefix?: string | React.ReactNode;
  postfix?: string | React.ReactNode;
}

export const ALLOW_NEGATIVE_AND_POSITIVE_FLOAT = /^[-+]?\d*[.,]?\d*$/;
export const ALLOW_NEGATIVE_AND_POSITIVE_PRICE = /^[-+]?\d*[.,]?\d{0,2}$/;
export const ALLOW_NEGATIVE_AND_POSITIVE_PRICE_6D = /^[-+]?\d*[.,]?\d{0,6}$/;
export const ALLOW_NEGATIVE_AND_POSITIVE_INT = /^[-+]?\d*$/;
export const ALLOW_POSITIVE_INT = /^\d*$/;
export const ALLOW_POSITIVE_INT_WITH_APOSTROPHE = /^\d*[']?\d*$/;
export const ALLOW_POSITIVE_FLOAT = /^\d*[.,]?\d*$/;
export const ALLOW_POSITIVE_FLOAT_DOT_PERCENT = /^\d*[.]?\d{0,2}$/;
export const ALLOW_POSITIVE_PRICE = /^\d*[.,]?\d{0,2}$/;
export const ALLOW_POSITIVE_PRICE_6D = /^\d*[.,]?\d{0,6}$/;

export const REMOVE_HTML_TAGS = /(<([^>]+)>)/gi;

type Type = 'text' | 'password' | 'email' | 'number';

export interface TextFieldProps
  extends FieldRenderProps<string | undefined, HTMLInputElement>,
    Omit<React.HTMLProps<HTMLInputElement>, 'as' | 'ref' | 'css'> {
  className?: string;
  settings?: Settings;
  type?: Type;
  inputRef?: React.Ref<HTMLInputElement>;
  dataTestId?: string;
}

const TextField: React.FC<TextFieldProps> = (props) => {
  const {
    className = '',
    settings = {
      floatOnly: false,
      intOnly: false,
      regexp: undefined,
      callback: undefined,
      maxLength: undefined,
      prefix: null,
      postfix: null,
    },
    input,
    meta,
    type = 'text',
    inputRef = null,
    dataTestId,
    ...other
  } = props;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-restricted-syntax
  const isOnChange = React.useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      if (settings.intOnly && !/^\d*$/.test(evt.target.value)) {
        return false;
      }
      if (settings.floatOnly && !/^\d*[.,]?\d*$/.test(evt.target.value)) {
        return false;
      }
      if (settings.regexp) {
        return new RegExp(settings.regexp).test(evt.target.value);
      }

      return true;
    },
    [settings.floatOnly, settings.intOnly, settings.regexp],
  );

  const length = input.value ? input.value.length : 0;

  return (
    <Container styled={{ prefix: settings.prefix, postfix: !!settings.maxLength || !!settings.postfix }} data-testid={dataTestId}>
      {settings.prefix && <Prefix styled={{ textarea: false, rows: 1 }}>{settings.prefix}</Prefix>}
      <Input
        className={className}
        maxLength={settings.maxLength}
        type={type}
        {...input}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const val = settings.callback?.(e.target.value) ?? e.target.value;
          if (settings.callback) {
            e.target.value = val;
          }
          return isOnChange(e) ? input.onChange(e) : undefined;
        }}
        ref={inputRef}
        loading={meta.validating}
        disabled={meta.validating || other.disabled}
        {...other}
      />
      {settings.maxLength && <Counter styled={{ textarea: false, rows: 1 }}>{`${length}/${settings.maxLength}`}</Counter>}
      {settings.postfix && !settings.maxLength && <Counter styled={{ textarea: false, rows: 1 }}>{settings.postfix}</Counter>}
    </Container>
  );
};

export default TextField;
